import { Component, NgZone, OnDestroy, OnInit } from '@angular/core'
import { collection, getDocs, onSnapshot, orderBy, query, where, Unsubscribe } from '@angular/fire/firestore'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { AdminClient, BroadLocation, CrmAssignee, FirestoreStructure, LeadClosedReason, LeadStatus, WatiTemplates } from '@parkupp/core'

// Contact type enum - matches @parkupp/core ContactType
export enum ContactType {
    USER = 'User',
    LEAD = 'Lead',
    LANDLORD = 'Landlord',
}
import { AdminClientService } from 'src/app/services/admin-client.service'
import { AdminService } from 'src/app/services/admin.service'
import { GlobalService } from 'src/app/services/global.service'
import Swal from 'sweetalert2'
import { VariableService } from 'src/app/services/variable.service'
import { AuthenticationService } from 'src/app/services/authentication.service'
import { BackendService } from 'src/app/services/backend.service'
import { CommentService } from 'src/app/services/comment.service'

@Component({
    selector: 'app-parker-crm',
    templateUrl: './parker-crm.component.html',
    styleUrls: ['./parker-crm.component.scss'],
})
export class ParkerCrm implements OnInit, OnDestroy {
    // Ordered list of lead statuses
    leadStatuses = [LeadStatus.NEW, LeadStatus.CONTACT_ATTEMPTED, LeadStatus.CONTACTED, LeadStatus.PROSPECTING, LeadStatus.NEGOTIATING, LeadStatus.CONVERTED, LeadStatus.CLOSED]
    crmAssignees: CrmAssignee[] = []
    crmAssigneeMap: { [key: string]: CrmAssignee } = {} // Map of UID to CrmAssignee for quick lookup
    contactTypes = Object.values(ContactType)
    adminClients: AdminClient[]
    filteredAdminClients: AdminClient[]
    statsAdminClients: AdminClient[] = []
    lastSyncedAt: Date

    // Real-time subscriptions
    private clientsUnsubscribe: Unsubscribe | null = null
    private statsUnsubscribe: Unsubscribe | null = null

    // Sales manager mode - if true, only show assigned leads
    isSalesManagerMode = false
    filterForm: FormGroup = new FormGroup({
        search: new FormControl(''),
        filterStatusCategory: new FormControl('active'),
        filterUserResponsible: new FormControl(''),
        filterDateRange: new FormControl('last3months'),
        customStartDate: new FormControl(''),
        customEndDate: new FormControl(''),
    })
    view: string
    searchReady: boolean = false

    sortColumn: string = ''
    sortDirection: 'asc' | 'desc' = 'asc'

    // Column configuration
    showColumnConfig: boolean = false

    // Mobile UI state
    showMobileFilters: boolean = false
    showMobileSettings: boolean = false
    allColumns = [
        { key: 'createdAt', label: 'Created' },
        { key: 'status', label: 'Type' },
        { key: 'leadStatus', label: 'Lead Status' },
        { key: 'userResponsible', label: 'User' },
        { key: 'numberOfBays', label: 'Bays' },
        { key: 'leadValue', label: 'Value' },
        { key: 'broadLocation', label: 'Location' },
        { key: 'firstName', label: 'Name / Company' },
        { key: 'email', label: 'Email' },
        { key: 'phoneNumber', label: 'Phone' },
        { key: 'notes', label: 'Notes' },
        { key: 'breachedSubscriptionsCount', label: 'Breached Subs' },
        { key: 'completedSubscriptionsCount', label: 'Completed Subs' },
        { key: 'activeSubscriptionExpiry', label: 'Current Sub' },
        { key: 'hasFutureSubscription', label: 'Future Sub' },
        { key: 'p_source', label: 'Source' },
        { key: 'p_campaign', label: 'Campaign' },
        { key: 'denormEnterpriseKey', label: 'Enterprise Source' },
    ]
    visibleColumns: { [key: string]: boolean } = {}

    // Lead profile panel
    showLeadDetail: boolean = false
    selectedLead: AdminClient | null = null

    // User list search
    userListSearch: string = ''
    selectedUserIds: string[] = []
    lastSelectedUserKey: string | null = null
    deletingUsers = false

    // New lead modal
    showNewLeadModal: boolean = false
    isCreatingLead: boolean = false
    newLeadForm: FormGroup = new FormGroup({
        firstName: new FormControl('', [Validators.required]),
        lastName: new FormControl(''),
        companyName: new FormControl(''),
        email: new FormControl(''),
        phoneNumber: new FormControl(''),
        numberOfBays: new FormControl(1),
        monthsRequired: new FormControl(null),
        broadLocation: new FormControl(''),
        userResponsible: new FormControl(''),
        contactType: new FormControl(ContactType.LEAD), // Default to Lead
        p_source: new FormControl('Manual'),
    })

    dateRangeOptions = [
        { value: 'last7days', label: 'Last 7 days' },
        { value: 'last30days', label: 'Last 30 days' },
        { value: 'last3months', label: 'Last 3 months' },
        { value: 'last6months', label: 'Last 6 months' },
        { value: 'last12months', label: 'Last 12 months' },
        { value: 'alltime', label: 'All time' },
        { value: 'custom', label: 'Custom range' },
    ]

    // Saved searches
    savedSearches: { name: string; url: string }[] = []
    showSavedSearchesDropdown: boolean = false

    // Help modal
    showHelpModal: boolean = false

    // Starred filter
    showStarredOnly: boolean = false

    // Tag filter
    allTags: string[] = []
    filterTags: string[] = []

    // Send template
    showSendTemplateSection: boolean = false
    selectedTemplate: string = ''
    isSendingTemplate: boolean = false
    crmWhatsappTemplates = [
        { value: WatiTemplates.CRM_NO_ANSWER, label: 'No Answer Follow-up' },
        { value: WatiTemplates.CRM_GOOGLE_REVIEW, label: 'Google Review Request' },
        { value: WatiTemplates.CRM_NEW_SUBSCRIPTION_CONFIRMATION, label: 'New Subscription Confirmation' },
    ]

    // Raw results from Firestore (before client-side filtering)
    private rawAdminClients: AdminClient[] = []
    // Track server-side query params to avoid unnecessary re-subscriptions
    private lastServerQueryKey: string = ''

    get isCustomDateRange(): boolean {
        return this.filterForm.get('filterDateRange')?.value === 'custom'
    }

    // Lead detail form options
    broadLocations = Object.values(BroadLocation)
    leadClosedReasons = Object.values(LeadClosedReason)
    leadSourceOptions = ['Manual', 'Website Form', 'Google', 'Cluster', 'Direct Marketing']
    LeadStatus = LeadStatus

    constructor(
        private adminSvc: AdminService,
        public globalService: GlobalService,
        private adminClientService: AdminClientService,
        private router: Router,
        private route: ActivatedRoute,
        private variableService: VariableService,
        private authService: AuthenticationService,
        private backendService: BackendService,
        private commentService: CommentService,
        private ngZone: NgZone
    ) {}

    ngOnInit(): void {
        const now = new Date()
        this.lastSyncedAt = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 5, 0, 0, 0)

        // Check if user is sales manager (not admin)
        this.isSalesManagerMode = this.authService.isSalesManager && !this.authService.isAdmin

        // Load column preferences from cookie
        this.loadColumnPreferences()

        // Load saved searches
        this.loadSavedSearches()

        // Load CRM assignees
        this.loadCrmAssignees()

        // Subscribe to route parameter changes to detect view switches
        this.route.params.subscribe((params) => {
            this.view = params['view']
            if (this.view === 'stats') {
                this.loadStatsClients()
            } else if (this.statsUnsubscribe) {
                this.statsUnsubscribe()
                this.statsUnsubscribe = null
            }
        })

        // Load filters from URL query params on init
        this.route.queryParams.subscribe((params) => {
            this.loadFiltersFromUrl(params)
        })

        this.getClientsAdmin()

        this.filterForm.valueChanges.subscribe(() => {
            const serverKey = this.getServerQueryKey()
            if (serverKey !== this.lastServerQueryKey) {
                this.getClientsAdmin()
            } else {
                this.applyClientSideFilters()
            }
            this.updateUrlWithFilters()
            if (this.view === 'stats') {
                this.loadStatsClients()
            }
        })
    }

    ngOnDestroy(): void {
        if (this.clientsUnsubscribe) {
            this.clientsUnsubscribe()
            this.clientsUnsubscribe = null
        }
        if (this.statsUnsubscribe) {
            this.statsUnsubscribe()
            this.statsUnsubscribe = null
        }
    }

    async loadCrmAssignees(): Promise<void> {
        const snapshot = await getDocs(query(collection(this.adminSvc.firestore, FirestoreStructure.CRM_ASSIGNEES), where('active', '==', true)))
        this.crmAssignees = snapshot.docs.map((doc) => new CrmAssignee(doc.data(), doc.id))

        // Build map for quick lookup
        this.crmAssigneeMap = {}
        this.crmAssignees.forEach((assignee) => {
            if (assignee.$key) {
                this.crmAssigneeMap[assignee.$key] = assignee
            }
        })
    }

    getAssigneeDisplayName(userResponsible: string | undefined): string {
        if (!userResponsible) return '-'
        const assignee = this.crmAssigneeMap[userResponsible]
        return assignee ? assignee.displayName : '-'
    }

    // URL Filter persistence
    loadFiltersFromUrl(params: any): void {
        const formValues: any = {}
        if (params['search']) formValues.search = params['search']
        if (params['statusCategory']) formValues.filterStatusCategory = params['statusCategory']
        if (params['user']) formValues.filterUserResponsible = params['user']
        if (params['dateRange']) formValues.filterDateRange = params['dateRange']
        if (params['startDate']) formValues.customStartDate = params['startDate']
        if (params['endDate']) formValues.customEndDate = params['endDate']

        if (params['sortCol']) this.sortColumn = params['sortCol']
        if (params['sortDir']) this.sortDirection = params['sortDir'] as 'asc' | 'desc'

        if (params['tags']) {
            this.filterTags = params['tags'].split(',').filter((t: string) => t)
        }

        if (Object.keys(formValues).length > 0) {
            this.filterForm.patchValue(formValues, { emitEvent: false })
        }
    }

    updateUrlWithFilters(): void {
        const formValues = this.filterForm.value
        const queryParams: any = {}

        if (formValues.search) queryParams.search = formValues.search
        if (formValues.filterStatusCategory && formValues.filterStatusCategory !== 'active') queryParams.statusCategory = formValues.filterStatusCategory
        if (formValues.filterUserResponsible) queryParams.user = formValues.filterUserResponsible
        if (formValues.filterDateRange && formValues.filterDateRange !== 'last12months') queryParams.dateRange = formValues.filterDateRange
        if (formValues.customStartDate) queryParams.startDate = formValues.customStartDate
        if (formValues.customEndDate) queryParams.endDate = formValues.customEndDate

        if (this.sortColumn) queryParams.sortCol = this.sortColumn
        if (this.sortColumn && this.sortDirection) queryParams.sortDir = this.sortDirection

        if (this.filterTags.length > 0) queryParams.tags = this.filterTags.join(',')

        this.router.navigate([], {
            relativeTo: this.route,
            queryParams,
            queryParamsHandling: 'merge',
            replaceUrl: true,
        })
    }

    onSortChange(event: { column: string; direction: 'asc' | 'desc' }): void {
        this.sortColumn = event.column
        this.sortDirection = event.direction
        this.updateUrlWithFilters()
    }

    // Saved Searches functionality
    loadSavedSearches(): void {
        const saved = localStorage.getItem('parkerCrmSavedSearches')
        if (saved) {
            try {
                this.savedSearches = JSON.parse(saved)
            } catch {
                this.savedSearches = []
            }
        }
    }

    async saveCurrentSearch(): Promise<void> {
        const currentUrl = window.location.href
        const { value: name } = await Swal.fire({
            title: 'Save Search',
            input: 'text',
            inputLabel: 'Enter a name for this search',
            inputPlaceholder: 'e.g., Hot leads in Cape Town',
            showCancelButton: true,
            inputValidator: (value) => {
                if (!value) {
                    return 'Please enter a name'
                }
                return null
            },
        })

        if (name) {
            this.savedSearches.push({ name, url: currentUrl })
            localStorage.setItem('parkerCrmSavedSearches', JSON.stringify(this.savedSearches))
            Swal.fire({
                icon: 'success',
                title: 'Search Saved',
                text: `"${name}" has been saved.`,
                timer: 1500,
                showConfirmButton: false,
            })
        }
    }

    loadSavedSearch(search: { name: string; url: string }): void {
        window.location.href = search.url
    }

    deleteSavedSearch(index: number, event: Event): void {
        event.stopPropagation()
        this.savedSearches.splice(index, 1)
        localStorage.setItem('parkerCrmSavedSearches', JSON.stringify(this.savedSearches))
    }

    toggleSavedSearchesDropdown(event?: Event): void {
        if (event) {
            event.stopPropagation()
        }
        this.showSavedSearchesDropdown = !this.showSavedSearchesDropdown

        // Close dropdown when clicking outside
        if (this.showSavedSearchesDropdown) {
            setTimeout(() => {
                const closeDropdown = (e: Event) => {
                    this.showSavedSearchesDropdown = false
                    document.removeEventListener('click', closeDropdown)
                }
                document.addEventListener('click', closeDropdown)
            }, 0)
        }
    }

    // Column configuration methods
    loadColumnPreferences(): void {
        const saved = this.getCookie('parkerCrmColumns')
        if (saved) {
            try {
                this.visibleColumns = JSON.parse(saved)
            } catch {
                this.setDefaultColumns()
            }
        } else {
            this.setDefaultColumns()
        }
    }

    setDefaultColumns(): void {
        this.allColumns.forEach((col) => {
            this.visibleColumns[col.key] = true
        })
    }

    saveColumnPreferences(): void {
        this.setCookie('parkerCrmColumns', JSON.stringify(this.visibleColumns), 365)
    }

    openColumnConfig(): void {
        this.showColumnConfig = true
    }

    closeColumnConfig(): void {
        this.showColumnConfig = false
        this.saveColumnPreferences()
    }

    toggleColumn(key: string): void {
        this.visibleColumns[key] = !this.visibleColumns[key]
    }

    selectAllColumns(): void {
        this.allColumns.forEach((col) => {
            this.visibleColumns[col.key] = true
        })
    }

    deselectAllColumns(): void {
        this.allColumns.forEach((col) => {
            this.visibleColumns[col.key] = false
        })
    }

    // Cookie helpers
    private getCookie(name: string): string {
        const nameEQ = name + '='
        const ca = document.cookie.split(';')
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i]
            while (c.charAt(0) === ' ') c = c.substring(1, c.length)
            if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
        }
        return ''
    }

    private setCookie(name: string, value: string, days: number): void {
        let expires = ''
        if (days) {
            const date = new Date()
            date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
            expires = '; expires=' + date.toUTCString()
        }
        document.cookie = name + '=' + (value || '') + expires + '; path=/'
    }

    // User list helpers
    getFilteredUserList(): AdminClient[] {
        if (!this.adminClients) return []
        if (!this.userListSearch) return this.adminClients
        const term = this.userListSearch.toLowerCase()
        return this.adminClients.filter(
            (c) =>
                c.searchableField?.toLowerCase().includes(term) ||
                c.firstName?.toLowerCase().includes(term) ||
                c.lastName?.toLowerCase().includes(term) ||
                c.companyName?.toLowerCase().includes(term) ||
                c.email?.toLowerCase().includes(term) ||
                c.phoneNumber?.includes(term)
        )
    }

    isUserSelected(client: AdminClient): boolean {
        return !!client.$key && this.selectedUserIds.includes(client.$key)
    }

    areAllVisibleUsersSelected(): boolean {
        const list = this.getFilteredUserList()
        return list.length > 0 && list.every((client) => !!client.$key && this.selectedUserIds.includes(client.$key))
    }

    onUserCheckboxClick(event: MouseEvent, client: AdminClient): void {
        event.stopPropagation()
        const key = client.$key
        if (!key) {
            return
        }

        const list = this.getFilteredUserList()
        const index = list.findIndex((item) => item.$key === key)
        if (index < 0) {
            return
        }

        if (event.shiftKey && this.lastSelectedUserKey) {
            event.preventDefault()
            const lastIndex = list.findIndex((item) => item.$key === this.lastSelectedUserKey)
            if (lastIndex >= 0) {
                const start = Math.min(lastIndex, index)
                const end = Math.max(lastIndex, index)
                const next = new Set(this.selectedUserIds)
                for (let i = start; i <= end; i++) {
                    const rangeKey = list[i].$key
                    if (rangeKey) {
                        next.add(rangeKey)
                    }
                }
                next.add(key)
                this.selectedUserIds = Array.from(next)
                this.lastSelectedUserKey = key
                const input = event.target as HTMLInputElement
                if (input) {
                    input.checked = true
                }
                return
            }
        }

        if (this.selectedUserIds.includes(key)) {
            this.selectedUserIds = this.selectedUserIds.filter((id) => id !== key)
        } else {
            this.selectedUserIds = [...this.selectedUserIds, key]
        }
        this.lastSelectedUserKey = key
    }

    toggleSelectAllVisibleUsers(event: Event): void {
        event.stopPropagation()
        const list = this.getFilteredUserList()
        if (this.areAllVisibleUsersSelected()) {
            const visible = new Set(list.map((client) => client.$key).filter(Boolean) as string[])
            this.selectedUserIds = this.selectedUserIds.filter((id) => !visible.has(id))
        } else {
            const next = new Set(this.selectedUserIds)
            list.forEach((client) => {
                if (client.$key) {
                    next.add(client.$key)
                }
            })
            this.selectedUserIds = Array.from(next)
        }
    }

    private getSelectedClients(): AdminClient[] {
        const byKey = new Map<string, AdminClient>()
        ;[...this.rawAdminClients, ...(this.adminClients || [])].forEach((client) => {
            if (client.$key) {
                byKey.set(client.$key, client)
            }
        })
        return this.selectedUserIds.map((id) => byKey.get(id)).filter((client): client is AdminClient => !!client)
    }

    private userHasSubscriptions(client: AdminClient): boolean {
        const summaries = client.subscriptionSummaries
        return (
            (summaries && Object.keys(summaries).length > 0) ||
            !!client.hasActiveSubscription ||
            !!client.hasFutureSubscription ||
            !!client.hasEverHadActiveOrCompletedSubscription ||
            (client.completedSubscriptionsCount || 0) > 0 ||
            (client.breachedSubscriptionsCount || 0) > 0
        )
    }

    private userSkipReason(client: AdminClient): string | null {
        if (client.$key && client.$key === this.authService.uid) {
            return 'this is your account'
        }
        if (this.userHasSubscriptions(client)) {
            return 'has subscriptions'
        }
        return null
    }

    private formatUserLabel(client: AdminClient): string {
        const name = [client.firstName, client.lastName].filter(Boolean).join(' ') || 'No Name'
        return client.email ? `${name} (${client.email})` : name
    }

    private escapeHtml(value: string): string {
        return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
    }

    private renderUserPreviewList(items: { label: string; reason?: string }[], limit = 8): string {
        const shown = items.slice(0, limit)
        const extra = items.length - shown.length
        const rows = shown
            .map((item) => {
                const reason = item.reason ? ` — ${this.escapeHtml(item.reason)}` : ''
                return `<li style="margin:0 0 4px;">${this.escapeHtml(item.label)}${reason}</li>`
            })
            .join('')
        const more = extra > 0 ? `<li style="margin:0;color:#6c757d;">+${extra} more</li>` : ''
        return `<ul style="margin:6px 0 0;padding-left:18px;text-align:left;">${rows}${more}</ul>`
    }

    async deleteSelectedUsers(): Promise<void> {
        if (this.selectedUserIds.length === 0 || this.deletingUsers) {
            return
        }

        const selected = this.getSelectedClients()
        const toDelete: AdminClient[] = []
        const toSkip: { client: AdminClient; reason: string }[] = []
        selected.forEach((client) => {
            const reason = this.userSkipReason(client)
            if (reason) {
                toSkip.push({ client, reason })
            } else {
                toDelete.push(client)
            }
        })

        const deleteIds = toDelete.map((client) => client.$key).filter((id): id is string => !!id)
        let html = `<p style="text-align:left;margin:0 0 12px;">This permanently deletes the account, profile, and CRM record.</p>`
        if (toDelete.length) {
            html += `<p style="text-align:left;margin:0;font-weight:600;">${toDelete.length} will be deleted</p>`
            html += this.renderUserPreviewList(toDelete.map((client) => ({ label: this.formatUserLabel(client) })))
        }
        if (toSkip.length) {
            html += `<p style="text-align:left;margin:16px 0 0;font-weight:600;">${toSkip.length} will not be deleted</p>`
            html += this.renderUserPreviewList(toSkip.map((item) => ({ label: this.formatUserLabel(item.client), reason: item.reason })))
        }

        const confirm = await Swal.fire({
            title: toDelete.length ? `Delete ${toDelete.length} user${toDelete.length === 1 ? '' : 's'}?` : 'Nothing to delete',
            html,
            icon: toDelete.length ? 'warning' : 'info',
            showCancelButton: true,
            showConfirmButton: toDelete.length > 0,
            confirmButtonText: `Delete ${toDelete.length}`,
            confirmButtonColor: '#dc3545',
            cancelButtonText: toDelete.length ? 'Cancel' : 'OK',
        })
        if (!confirm.isConfirmed || deleteIds.length === 0) {
            return
        }

        this.deletingUsers = true
        try {
            const batchSize = 100
            let deleted = 0
            let skipped = 0
            for (let i = 0; i < deleteIds.length; i += batchSize) {
                const batch = deleteIds.slice(i, i + batchSize)
                const result = await this.backendService.deleteCrmUsers(batch)
                deleted += result.deleted || 0
                skipped += result.skipped || 0
            }
            this.selectedUserIds = this.selectedUserIds.filter((id) => !deleteIds.includes(id))
            const extraSkipped = skipped ? ` ${skipped} more were skipped by the server.` : ''
            await Swal.fire({
                icon: deleted ? 'success' : 'info',
                title: 'Done',
                text: `${deleted} deleted.${extraSkipped}`,
            })
        } catch (error: any) {
            await Swal.fire({
                icon: 'error',
                title: 'Delete failed',
                text: error?.message || 'Unable to delete users.',
            })
        } finally {
            this.deletingUsers = false
        }
    }

    getInitials(client: AdminClient): string {
        const first = (client.firstName || '?')[0].toUpperCase()
        const last = (client.lastName || '')[0]?.toUpperCase() || ''
        return first + last
    }

    getInitialColor(client: AdminClient): string {
        const str = (client.firstName || '') + (client.lastName || '')
        const hash = str.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
        const colors = ['#3e8bff', '#4bd181', '#f59e0b', '#ef4444', '#6366f1', '#ec4899', '#14b8a6', '#8b5cf6']
        return colors[hash % colors.length]
    }

    // Board status change
    async onBoardStatusChange(event: { client: AdminClient; newStatus: LeadStatus }): Promise<void> {
        event.client.leadStatus = event.newStatus
        await this.adminClientService.update(event.client)
    }

    // Lead detail methods
    openLeadDetail(lead: AdminClient): void {
        this.selectedLead = lead
        this.showLeadDetail = true
    }

    closeLeadDetail(): void {
        this.showLeadDetail = false
        this.selectedLead = null
    }

    async updateLeadField(field: string, value: any): Promise<void> {
        if (!this.selectedLead) return
        ;(this.selectedLead as any)[field] = value
        await this.adminClientService.update(this.selectedLead)
    }

    async updateLeadAddress(event: { fullAddress: string; latitude: number; longitude: number }): Promise<void> {
        if (!this.selectedLead) return
        this.selectedLead.fullAddress = event.fullAddress
        this.selectedLead.latitude = event.latitude
        this.selectedLead.longitude = event.longitude
        await this.adminClientService.update(this.selectedLead)
    }

    async updateUserResponsible(newUserId: string): Promise<void> {
        if (!this.selectedLead) return

        const previousUserId = this.selectedLead.userResponsible
        this.selectedLead.userResponsible = newUserId || undefined
        await this.adminClientService.update(this.selectedLead)

        // If sales manager mode and user changed assignment away from themselves, close the popup
        // because they will no longer have permission to view this lead
        if (this.isSalesManagerMode && newUserId !== this.authService.uid) {
            this.closeLeadDetail()
        }
    }

    callPhone(phoneNumber: string): void {
        window.open(`tel:${phoneNumber.replace(/\s+/g, '')}`, '_blank')
    }

    sendWhatsApp(phoneNumber: string): void {
        window.open(`https://wa.me/${phoneNumber.replace(/\s+/g, '')}`, '_blank')
    }

    sendEmail(email: string): void {
        window.open(`mailto:${email}`, '_blank')
    }

    onSendTemplateFromPanel(event: { template: string; parameters: { name: string; value: string }[] }): void {
        this.selectedTemplate = event.template
        this.sendWhatsAppTemplate(event.parameters)
    }

    async sendWhatsAppTemplate(parameters: { name: string; value: string }[] = []): Promise<void> {
        if (!this.selectedLead?.phoneNumber || !this.selectedTemplate || this.isSendingTemplate) return

        this.isSendingTemplate = true

        try {
            const templateLabel = this.crmWhatsappTemplates.find(t => t.value === this.selectedTemplate)?.label || this.selectedTemplate

            await this.backendService.sendCrmWhatsappTemplate(
                this.selectedLead.phoneNumber,
                this.selectedTemplate as WatiTemplates,
                parameters
            )

            if (this.selectedLead.$key) {
                const senderName = this.authService.user?.displayName || this.authService.user?.email || 'Unknown'
                await this.commentService.addSystemComment(
                    this.selectedLead.$key,
                    `WhatsApp template "${templateLabel}" sent by ${senderName}`
                )
            }

            Swal.fire({
                icon: 'success',
                title: 'Message Sent',
                text: `WhatsApp template sent to ${this.selectedLead.firstName || 'contact'}.`,
                timer: 2000,
                showConfirmButton: false,
            })

            this.showSendTemplateSection = false
            this.selectedTemplate = ''
        } catch (error) {
            console.error('Error sending WhatsApp template:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to send WhatsApp message. Please try again.',
            })
        } finally {
            this.isSendingTemplate = false
        }
    }

    // New Lead Modal methods
    openNewLeadModal(): void {
        this.showNewLeadModal = true
        this.newLeadForm.reset({
            firstName: '',
            lastName: '',
            companyName: '',
            email: '',
            phoneNumber: '',
            numberOfBays: 1,
            monthsRequired: null,
            broadLocation: '',
            userResponsible: '',
            contactType: ContactType.LEAD,
            p_source: 'Manual',
        })
    }

    closeNewLeadModal(): void {
        this.showNewLeadModal = false
        this.isCreatingLead = false
    }

    async createNewLead(): Promise<void> {
        if (!this.newLeadForm.valid || this.isCreatingLead) return

        this.isCreatingLead = true

        try {
            const formValues = this.newLeadForm.value

            // Build searchable field
            const searchableField = [formValues.firstName, formValues.lastName, formValues.companyName, formValues.email, formValues.phoneNumber]
                .filter((v) => v)
                .join(' ')
                .toLowerCase()

            const newLead = new AdminClient({
                firstName: formValues.firstName || '',
                lastName: formValues.lastName || '',
                companyName: formValues.companyName || '',
                email: formValues.email || '',
                phoneNumber: formValues.phoneNumber || '',
                numberOfBays: formValues.numberOfBays || 0,
                monthsRequired: formValues.monthsRequired || null,
                broadLocation: formValues.broadLocation || '',
                userResponsible: formValues.userResponsible || '',
                contactType: formValues.contactType || ContactType.LEAD,
                p_source: formValues.p_source || 'Manual',
                leadStatus: LeadStatus.NEW,
                isManualLead: true,
                searchableField: searchableField,
                // Default values for a new manual lead
                hasActiveSubscription: false,
                hasFutureSubscription: false,
                hasEverHadActiveOrCompletedSubscription: false,
                breachedSubscriptionsCount: 0,
                completedSubscriptionsCount: 0,
                noAuth: true,
                emailVerified: false,
                authTypes: [],
            })

            await this.adminClientService.createManualLead(newLead)

            this.closeNewLeadModal()

            Swal.fire({
                icon: 'success',
                title: 'Lead Created',
                text: `${formValues.firstName} ${formValues.lastName || ''} has been added as a new lead.`,
                timer: 2000,
                showConfirmButton: false,
            })

            // No need to manually refresh - real-time subscription will update automatically
        } catch (error) {
            console.error('Error creating lead:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to create lead. Please try again.',
            })
        } finally {
            this.isCreatingLead = false
        }
    }

    onFilter() {
        const serverKey = this.getServerQueryKey()
        if (serverKey !== this.lastServerQueryKey) {
            this.getClientsAdmin()
        } else {
            this.applyClientSideFilters()
        }
    }

    private getServerQueryKey(): string {
        const filterDateRange = this.filterForm.get('filterDateRange')?.value
        const statusCategory = this.filterForm.get('filterStatusCategory')?.value || 'active'
        const customStart = this.filterForm.get('customStartDate')?.value || ''
        const customEnd = this.filterForm.get('customEndDate')?.value || ''
        return `${filterDateRange}|${statusCategory}|${customStart}|${customEnd}`
    }

    private applyClientSideFilters(): void {
        const searchTerm = this.filterForm.get('search')?.value?.toLowerCase()
        const filterUserResponsible = this.filterForm.get('filterUserResponsible')?.value
        const statusCategory = this.filterForm.get('filterStatusCategory')?.value || 'active'

        let clients = this.rawAdminClients.filter((client) => {
            if (searchTerm && client.searchableField && !client.searchableField.toLowerCase().includes(searchTerm)) {
                return false
            }

            if (filterUserResponsible && !this.isSalesManagerMode && client.userResponsible !== filterUserResponsible) {
                return false
            }

            if (statusCategory === 'active' && (client.leadStatus === LeadStatus.CLOSED || client.leadStatus === LeadStatus.CONVERTED)) {
                return false
            }

            if (this.showStarredOnly && !client.isStarred) {
                return false
            }

            if (this.filterTags.length > 0 && !this.filterTags.every((tag) => this.getClientTags(client).includes(tag))) {
                return false
            }

            return true
        })

        this.adminClients = clients
        this.filteredAdminClients = clients

        if (this.selectedLead && this.selectedLead.$key) {
            const updatedLead = clients.find((c) => c.$key === this.selectedLead!.$key)
            if (updatedLead) {
                this.selectedLead = updatedLead
            }
        }
    }

    switchView(view: string) {
        this.router.navigate(['/admin/parker-crm', view])
    }

    getDateRangeStart(dateRange: string): Date | null {
        const now = new Date()
        switch (dateRange) {
            case 'last7days':
                return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
            case 'last30days':
                return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
            case 'last3months':
                return new Date(now.getFullYear(), now.getMonth() - 3, now.getDate())
            case 'last6months':
                return new Date(now.getFullYear(), now.getMonth() - 6, now.getDate())
            case 'last12months':
                return new Date(now.getFullYear(), now.getMonth() - 12, now.getDate())
            case 'custom':
                const customStart = this.filterForm.get('customStartDate')?.value
                return customStart ? new Date(customStart) : null
            case 'alltime':
            default:
                return null
        }
    }

    getDateRangeEnd(dateRange: string): Date | null {
        if (dateRange === 'custom') {
            const customEnd = this.filterForm.get('customEndDate')?.value
            if (customEnd) {
                // Set to end of day
                const endDate = new Date(customEnd)
                endDate.setHours(23, 59, 59, 999)
                return endDate
            }
        }
        return null
    }

    clearFilters(): void {
        this.filterForm.patchValue({
            search: '',
            filterStatusCategory: 'active',
            filterUserResponsible: '',
            filterDateRange: 'last12months',
            customStartDate: '',
            customEndDate: '',
        })

        // Clear starred filter
        this.showStarredOnly = false

        // Clear tag filter
        this.filterTags = []

        // Clear sort state
        this.sortColumn = ''
        this.sortDirection = 'asc'

        // Clear URL query params
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {},
            replaceUrl: true,
        })
    }

    getClientsAdmin() {
        if (this.clientsUnsubscribe) {
            this.clientsUnsubscribe()
            this.clientsUnsubscribe = null
        }

        this.lastServerQueryKey = this.getServerQueryKey()

        let clientsPrivateRef = query(collection(this.adminSvc.firestore, FirestoreStructure.ADMIN_CLIENTS))

        const filterDateRange = this.filterForm.get('filterDateRange')?.value
        const dateRangeStart = this.getDateRangeStart(filterDateRange)
        const dateRangeEnd = this.getDateRangeEnd(filterDateRange)
        const statusCategory = this.filterForm.get('filterStatusCategory')?.value || 'active'

        // Server-side: single-status category filter when closed or converted
        const serverSideStatus = statusCategory === 'closed' ? LeadStatus.CLOSED : statusCategory === 'converted' ? LeadStatus.CONVERTED : null

        if (this.isSalesManagerMode) {
            clientsPrivateRef = query(clientsPrivateRef, where('userResponsible', '==', this.authService.uid))
        }

        if (dateRangeStart) {
            if (serverSideStatus) {
                if (dateRangeEnd) {
                    clientsPrivateRef = query(clientsPrivateRef, where('leadStatus', '==', serverSideStatus), where('createdAt', '>=', dateRangeStart), where('createdAt', '<=', dateRangeEnd), orderBy('createdAt', 'desc'))
                } else {
                    clientsPrivateRef = query(clientsPrivateRef, where('leadStatus', '==', serverSideStatus), where('createdAt', '>=', dateRangeStart), orderBy('createdAt', 'desc'))
                }
            } else {
                if (dateRangeEnd) {
                    clientsPrivateRef = query(clientsPrivateRef, where('createdAt', '>=', dateRangeStart), where('createdAt', '<=', dateRangeEnd), orderBy('createdAt', 'desc'))
                } else {
                    clientsPrivateRef = query(clientsPrivateRef, where('createdAt', '>=', dateRangeStart), orderBy('createdAt', 'desc'))
                }
            }
        } else {
            if (serverSideStatus) {
                clientsPrivateRef = query(clientsPrivateRef, where('leadStatus', '==', serverSideStatus), orderBy('createdAt', 'desc'))
            } else {
                clientsPrivateRef = query(clientsPrivateRef, orderBy('createdAt', 'desc'))
            }
        }

        this.clientsUnsubscribe = onSnapshot(
            clientsPrivateRef,
            (snapshot) => {
                this.ngZone.run(() => {
                    this.rawAdminClients = snapshot.docs.map((doc) => {
                        return new AdminClient({
                            ...doc.data(),
                            $key: doc.id,
                        })
                    })

                    this.rebuildAllTags()
                    this.applyClientSideFilters()
                    this.searchReady = true
                })
            },
            (error) => {
                console.error('CRM onSnapshot error:', error)
                this.ngZone.run(() => {
                    this.searchReady = true
                })
            }
        )
    }

    loadStatsClients() {
        if (this.statsUnsubscribe) {
            this.statsUnsubscribe()
            this.statsUnsubscribe = null
        }

        let statsRef = query(collection(this.adminSvc.firestore, FirestoreStructure.ADMIN_CLIENTS))

        const filterDateRange = this.filterForm.get('filterDateRange')?.value
        const dateRangeStart = this.getDateRangeStart(filterDateRange)
        const dateRangeEnd = this.getDateRangeEnd(filterDateRange)

        if (dateRangeStart) {
            if (dateRangeEnd) {
                statsRef = query(statsRef, where('createdAt', '>=', dateRangeStart), where('createdAt', '<=', dateRangeEnd), orderBy('createdAt', 'desc'))
            } else {
                statsRef = query(statsRef, where('createdAt', '>=', dateRangeStart), orderBy('createdAt', 'desc'))
            }
        } else {
            statsRef = query(statsRef, orderBy('createdAt', 'desc'))
        }

        this.statsUnsubscribe = onSnapshot(
            statsRef,
            (snapshot) => {
                this.ngZone.run(() => {
                    this.statsAdminClients = snapshot.docs.map((doc) => {
                        return new AdminClient({
                            ...doc.data(),
                            $key: doc.id,
                        })
                    })
                    this.searchReady = true
                })
            },
            (error) => {
                console.error('CRM stats onSnapshot error:', error)
            }
        )
    }

    toggleStarredFilter(): void {
        this.showStarredOnly = !this.showStarredOnly
        this.applyClientSideFilters()
    }

    // Tag management - uses (client as any).tags to safely access the field
    // since it may not be in the compiled type declaration cache
    private getClientTags(client: AdminClient): string[] {
        return (client as any).tags || []
    }

    private setClientTags(client: AdminClient, tags: string[]): void {
        ;(client as any).tags = tags
    }

    private rebuildAllTags(): void {
        const tagSet = new Set<string>()
        this.rawAdminClients.forEach((client) => {
            this.getClientTags(client).forEach((tag: string) => tagSet.add(tag))
        })
        this.allTags = Array.from(tagSet).sort()
    }

    onTagDropdownChange(value: string): void {
        if (value) {
            this.filterTags = [value]
        } else {
            this.filterTags = []
        }
        this.applyClientSideFilters()
        this.updateUrlWithFilters()
    }

    toggleTagFilter(tag: string): void {
        const idx = this.filterTags.indexOf(tag)
        if (idx >= 0) {
            this.filterTags.splice(idx, 1)
        } else {
            this.filterTags.push(tag)
        }
        this.filterTags = [...this.filterTags]
        this.applyClientSideFilters()
        this.updateUrlWithFilters()
    }

    clearTagFilter(): void {
        this.filterTags = []
        this.applyClientSideFilters()
        this.updateUrlWithFilters()
    }

    async addTagToLead(tag: string): Promise<void> {
        if (!this.selectedLead) return
        const currentTags = this.getClientTags(this.selectedLead)
        if (currentTags.includes(tag)) return
        this.setClientTags(this.selectedLead, [...currentTags, tag])
        await this.adminClientService.update(this.selectedLead)
    }

    async removeTagFromLead(tag: string): Promise<void> {
        if (!this.selectedLead) return
        this.setClientTags(this.selectedLead, this.getClientTags(this.selectedLead).filter((t: string) => t !== tag))
        await this.adminClientService.update(this.selectedLead)
    }

    async createAndAddTag(): Promise<void> {
        if (!this.selectedLead) return
        const { value: tagName } = await Swal.fire({
            title: 'Add Tag',
            input: 'text',
            inputLabel: 'Tag name',
            inputPlaceholder: 'e.g. shortlisted',
            showCancelButton: true,
            inputValidator: (value) => {
                if (!value?.trim()) return 'Please enter a tag name'
                return null
            },
        })
        if (tagName) {
            await this.addTagToLead(tagName.trim().toLowerCase())
        }
    }

    // Mobile-specific methods
    async toggleStar(client: AdminClient) {
        client.isStarred = !client.isStarred
        await this.adminClientService.update(client)
    }

    onMobileCardContext(event: MouseEvent, client: AdminClient) {
        this.globalService.contextMenuItems = []
        this.globalService.showContextMenu(event)

        const leadStatuses = [LeadStatus.NEW, LeadStatus.CONTACT_ATTEMPTED, LeadStatus.CONTACTED, LeadStatus.PROSPECTING, LeadStatus.NEGOTIATING, LeadStatus.CONVERTED, LeadStatus.CLOSED]

        const leadSubMenuItems: any = leadStatuses.map((status) => {
            return {
                label: status,
                action: () => this.updateLeadStatusMobile(client, status),
            }
        })

        this.globalService.contextMenuItems.push({
            label: 'Update Status',
            subMenuItems: leadSubMenuItems,
        })

        const leadUserSubMenuItems: any = this.crmAssignees.map((assignee) => {
            return {
                label: assignee.displayName,
                action: () => this.updateLeadUserMobile(client, assignee.$key),
            }
        })
        if (!this.isSalesManagerMode) {
            leadUserSubMenuItems.push({
                label: 'None',
                action: () => this.updateLeadUserMobile(client, null),
            })
        }
        this.globalService.contextMenuItems.push({
            label: 'Assign User',
            subMenuItems: leadUserSubMenuItems,
        })
    }

    async updateLeadStatusMobile(client: AdminClient, status: LeadStatus) {
        client.leadStatus = status
        await this.adminClientService.update(client)
    }

    async updateLeadUserMobile(client: AdminClient, userId: string | null | undefined) {
        client.userResponsible = userId || undefined
        await this.adminClientService.update(client)
    }

    getSubscriptionSummaries(): any[] {
        const summaries = (this.selectedLead as any)?.subscriptionSummaries
        if (!summaries) return []
        return Object.entries(summaries)
            .map(([key, value]: [string, any]) => ({ key, status: value.status, startDate: value.startDate, endDate: value.endDate, period: value.period, baysBooked: value.baysBooked, createdAt: value.createdAt }))
            .sort((a, b) => {
                const aTime = a.createdAt?.seconds || (a.createdAt instanceof Date ? a.createdAt.getTime() / 1000 : 0)
                const bTime = b.createdAt?.seconds || (b.createdAt instanceof Date ? b.createdAt.getTime() / 1000 : 0)
                return bTime - aTime
            })
    }

    getStatusClass(status: string): string {
        switch (status) {
            case 'Active':
                return 'sub-status-badge--active'
            case 'Completed':
                return 'sub-status-badge--completed'
            case 'Client Breached Non Payment':
                return 'sub-status-badge--breached'
            case 'Client Cancelled':
            case 'Merchant Rejected':
            case 'Expired':
            case 'Mutually Terminated':
                return 'sub-status-badge--cancelled'
            case 'Payment Initiated':
            case 'Awaiting Approval':
            case 'Accepted – payment processing':
            case 'Pending Client Approval':
            case 'Renewing':
                return 'sub-status-badge--pending'
            default:
                return 'sub-status-badge--default'
        }
    }
}
